import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, CircularProgress } from '@mui/material';
import { isAdminDomain, redirectToDomain } from '../utils/domainUtils';

interface PrivateRouteProps {
  children: React.ReactNode;
  requireAgentSelection?: boolean;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, requireAgentSelection = true }) => {
  const { currentUser, userProfile, userPermissions, selectedAgent, loading } = useAuth();
  const location = useLocation();

  // Show loading indicator while checking auth state
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  // Only redirect if we're sure there's no user
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user's email is verified
  if (!userProfile?.emailVerified) {
    return <Navigate to="/verify-email" state={{ from: location }} replace />;
  }

  // Domain-based access control
  const isAdmin = isAdminDomain();
  
  // Super admins can access all pages on admin domain
  if (isAdmin && userProfile.role === 'super_admin') {
    return <>{children}</>;
  }
  
  // Agents can only access main domain
  if (!isAdmin && userProfile.role === 'admin') {
    // Check if agent selection is required and user hasn't selected one
    if (requireAgentSelection && !selectedAgent) {
      return <Navigate to="/post-login" state={{ from: location }} replace />;
    }
    return <>{children}</>;
  }
  
  // Access denied - redirect to appropriate domain
  if (isAdmin && userProfile.role === 'admin') {
    // Agent trying to access admin domain - redirect to main domain
    redirectToDomain('admin', location.pathname);
    return null;
  } else if (!isAdmin && userProfile.role === 'super_admin') {
    // Super admin on main domain - redirect to admin domain
    redirectToDomain('super_admin', location.pathname);
    return null;
  }

  return <>{children}</>;
};

export default PrivateRoute; 